import { Fragment, useState } from "react";
import type { ReactNode } from "react";
import { useChat } from "../../hooks/useChat";
import { ToolCallBubble } from "./ToolCallBubble";
import { useUIStore } from "../../store/uiStore";
import { chatPalette, type ChatPalette } from "../../lib/chatTheme";
import { downloadTranscript } from "../../lib/transcript";

// Lightweight inline-markdown renderer: handles **bold**, *italic*, `code`,
// and bare http(s) links. Emits React nodes (no innerHTML) so it's safe.
// Keep this small — it's not a full markdown parser.
const INLINE_RE = /(\*\*[^*\n]+\*\*|\*[^*\n]+\*|`[^`\n]+`|https?:\/\/\S+)/g;

function formatInline(text: string, palette: ChatPalette): ReactNode {
  const parts = text.split(INLINE_RE);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith("*") && part.endsWith("*") && part.length > 2) {
      return <em key={i}>{part.slice(1, -1)}</em>;
    }
    if (part.startsWith("`") && part.endsWith("`") && part.length > 2) {
      return (
        <code key={i} style={{ background: palette.toolBubbleBg, padding: "0 4px", borderRadius: 3, fontSize: "0.92em" }}>
          {part.slice(1, -1)}
        </code>
      );
    }
    if (/^https?:\/\//.test(part)) {
      return (
        <a key={i} href={part} target="_blank" rel="noopener noreferrer" style={{ color: palette.userLabel }}>
          {part}
        </a>
      );
    }
    return <Fragment key={i}>{part}</Fragment>;
  });
}

function renderContent(text: string, palette: ChatPalette): ReactNode {
  return text.split(/\n/).map((line, i, arr) => (
    <Fragment key={i}>
      {formatInline(line, palette)}
      {i < arr.length - 1 && <br />}
    </Fragment>
  ));
}

const EXAMPLE_QUERIES: Array<{ id: string; label: string; text: string }> = [
  { id: "Q1", label: "What's the risk?", text: "What is the wildfire risk for this cell?" },
  { id: "Q2", label: "Why flagged?",      text: "Why is this cell flagged?" },
  { id: "Q3", label: "Who lives here?",   text: "Who lives here? What does the demographic profile look like?" },
  { id: "Q4", label: "Recommended action?", text: "What is the recommended action for emergency dispatchers?" },
  { id: "Q5", label: "How confident?",    text: "How confident is the model in this prediction? What is its uncertainty?" },
];

interface Props {
  cellId: string;
  tickId: string;
  cellContext?: Record<string, unknown>;
}

export function ChatPanel({ cellId, tickId, cellContext }: Props) {
  const theme = useUIStore((s) => s.theme);
  const palette = chatPalette(theme);
  const { messages, send, isStreaming } = useChat({ cellId, tickId, cellContext });
  const [input, setInput] = useState("");

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;
    send(input);
    setInput("");
  };

  const canDownload = messages.length > 0 && !isStreaming;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: 13, fontWeight: 600, color: palette.headingText }}>
          Ask the Report Agent
        </h3>
        <button
          type="button"
          onClick={() => downloadTranscript({ cellId, tickId, messages })}
          disabled={!canDownload}
          aria-label="Download conversation as .txt"
          title={canDownload ? "Download conversation as .txt" : "Ask something first, then download the transcript"}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "3px 8px",
            fontSize: 11,
            background: palette.chipBg,
            color: palette.chipText,
            border: `1px solid ${palette.chipBorder}`,
            borderRadius: 4,
            // Keep the button visually present even when disabled so users
            // notice the affordance — only the cursor + opacity hint at
            // disabled-ness, no "ghosted away" effect that vanishes in dark.
            cursor: canDownload ? "pointer" : "not-allowed",
            opacity: canDownload ? 1 : 0.78,
          }}
        >
          <DownloadIcon />
          .txt
        </button>
      </div>

      {/* AI disclaimer — shown above every conversation. Edit the copy here. */}
      <div
        style={{
          fontSize: 11,
          lineHeight: 1.35,
          color: palette.metaText,
          fontStyle: "italic",
        }}
      >
        AI-generated guidance — may be inaccurate. Not a substitute for official
        emergency services; verify before acting.
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
        {EXAMPLE_QUERIES.map((q) => (
          <button
            key={q.id}
            type="button"
            disabled={isStreaming}
            onClick={() => send(q.text)}
            style={{
              padding: "4px 8px",
              fontSize: 11,
              border: `1px solid ${palette.chipBorder}`,
              borderRadius: 12,
              background: palette.chipBg,
              cursor: isStreaming ? "not-allowed" : "pointer",
              color: palette.chipText,
            }}
            title={q.text}
          >
            {q.label}
          </button>
        ))}
      </div>

      <div
        data-testid="chat-message-list"
        style={{
          maxHeight: "min(70vh, 800px)",
          minHeight: 360,
          overflowY: "auto",
          border: `1px solid ${palette.listBorder}`,
          borderRadius: 6,
          padding: 8,
          background: palette.listBg,
          fontSize: 13,
        }}
      >
        {messages.length === 0 && (
          <div style={{ color: palette.listEmptyText, fontSize: 12, textAlign: "center", padding: 12 }}>
            No conversation yet. Ask a question or pick an example above.
          </div>
        )}
        {messages.map((msg, idx) => {
          const isLast = idx === messages.length - 1;
          const isAssistant = msg.role === "assistant";
          const isAssistantStreaming = isAssistant && isLast && isStreaming;
          const hasToolCalls = msg.toolCalls && msg.toolCalls.length > 0;
          return (
            <div key={idx} style={{ marginBottom: 12 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginBottom: 4,
                  fontSize: 12,
                  fontWeight: 600,
                  color: isAssistant ? palette.assistantLabel : palette.userLabel,
                }}
              >
                <span>{isAssistant ? "🤖 Report Agent" : "You"}</span>
                {isAssistantStreaming && (
                  <span style={{ color: palette.metaText, fontStyle: "italic", fontWeight: 400 }}>
                    · thinking…
                  </span>
                )}
              </div>
              {hasToolCalls && (
                <div>
                  {msg.toolCalls!.map((tc, i) => (
                    <ToolCallBubble key={tc.call_id ?? i} call={tc} />
                  ))}
                </div>
              )}
              {msg.content && (
                <div
                  style={{
                    padding: "6px 10px",
                    background: isAssistant ? palette.assistantBubbleBg : palette.userBubbleBg,
                    color: isAssistant ? palette.assistantBubbleText : palette.userBubbleText,
                    borderRadius: 6,
                    whiteSpace: "pre-wrap",
                    marginTop: 6,
                    lineHeight: 1.4,
                  }}
                >
                  {isAssistant ? renderContent(msg.content, palette) : msg.content}
                </div>
              )}
              {isAssistant && !msg.content && !hasToolCalls && isAssistantStreaming && (
                <div style={{ color: palette.metaText, fontSize: 11, fontStyle: "italic", padding: "4px 10px" }}>
                  Waiting for first response…
                </div>
              )}
              {isAssistant && !msg.content && hasToolCalls && !isStreaming && (
                <div style={{ color: palette.metaText, fontSize: 11, fontStyle: "italic", padding: "4px 10px" }}>
                  (Tools above completed but the agent did not produce a final reply.)
                </div>
              )}
            </div>
          );
        })}
      </div>

      <form onSubmit={onSubmit} style={{ display: "flex", gap: 6 }}>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about risk, equity, weather..."
          disabled={isStreaming}
          style={{
            flex: 1,
            padding: "6px 8px",
            fontSize: 13,
            border: `1px solid ${palette.inputBorder}`,
            borderRadius: 4,
            background: palette.inputBg,
            color: palette.inputText,
          }}
        />
        <button
          type="submit"
          disabled={isStreaming || !input.trim()}
          style={{
            padding: "6px 12px",
            fontSize: 13,
            background: palette.sendBg,
            color: palette.sendText,
            border: "none",
            borderRadius: 4,
            cursor: isStreaming ? "not-allowed" : "pointer",
          }}
        >
          {isStreaming ? "…" : "Send"}
        </button>
      </form>
    </div>
  );
}

function DownloadIcon() {
  return (
    <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}
